import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';

import {
  staticDataCountriesMapper,
  staticDataCompanyTypesMapper,
  staticDataIndustriesMapper,
} from 'utils/functions/objectMapper';
import { trimText, detectFileTypeFromExtension } from 'utils/functions/general';
import { companyStatusEnum, companyStatusColorEnum } from 'modules/companyActivation/enums';
import { fileTypeEnum } from 'modules/common/enums';
import companyActivationStrings from 'modules/companyActivation/strings';
import Popup from 'modules/common/components/Popup';
import Loader from 'modules/common/components/Loader';
import SectionDivider from 'modules/common/components/SectionDivider';

import {
  Wrapper,
  GridWrapper,
  Header,
  Divider,
  ItemColumn,
  ItemRow,
  ItemWrapper,
  ItemTextWrapper,
  ItemText,
  ItemSubText,
  ItemClick,
  ItemTag,
  /* ####################### */
  ItemBtn,
  ActionsPopupWrapper,
  ActionsPopupTitleWrapper,
  ActionsPopupTitle,
  ActionsPopupBtnWrapper,
  ActionsPopupYesBtn,
  ActionsPopupNoBtn,
} from './CompanyActivationList.style';

const CompanyActionsPopup = ({
  themeContext,
  isActionPopupLoading,
  actionStatus,
  handleActionPopupConfirmBtnClick,
  handleActionPopupDismissBtnClick,
}) => (
  <Popup handleCloseBtnClick={() => handleActionPopupDismissBtnClick()}>
    <ActionsPopupWrapper>
      <ActionsPopupTitleWrapper>
        <ActionsPopupTitle>
          {companyActivationStrings.companyActivationList.actionPopupTitle(actionStatus)}
        </ActionsPopupTitle>
      </ActionsPopupTitleWrapper>
      <SectionDivider
        gap={16}
        withSeparator
        separatorGap={16}
        borderColor={themeContext.colors.grey004}
      />
      <ActionsPopupBtnWrapper>
        {(isActionPopupLoading && <Loader width={28} height={28} thickness={4} />) || (
          <>
            <ActionsPopupYesBtn as="button" onClick={() => handleActionPopupConfirmBtnClick()}>
              {companyActivationStrings.companyActivationList.actionPopupYesBtn}
            </ActionsPopupYesBtn>
            <ActionsPopupNoBtn as="button" onClick={() => handleActionPopupDismissBtnClick()}>
              {companyActivationStrings.companyActivationList.actionPopupNoBtn}
            </ActionsPopupNoBtn>
          </>
        )}
      </ActionsPopupBtnWrapper>
    </ActionsPopupWrapper>
  </Popup>
);
CompanyActionsPopup.propTypes = {
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      grey004: PropTypes.string,
    }),
  }).isRequired,
  isActionPopupLoading: PropTypes.bool.isRequired,
  actionStatus: PropTypes.string.isRequired,
  handleActionPopupConfirmBtnClick: PropTypes.func.isRequired,
  handleActionPopupDismissBtnClick: PropTypes.func.isRequired,
};

/* ############################################################################### */
/* ############################################################################### */
/* ############################################################################### */

const CompanyActivationList = ({
  companyActivationListArr,
  isActionPopupOpen,
  isActionPopupLoading,
  actionPopupStatus,
  handleTextToCopyToClipboardClick,
  handlePreviewFileClick,
  handleActionBtnClick,
  handleActionPopupConfirmBtnClick,
  handleActionPopupDismissBtnClick,
}) => {
  const { staticData } = useSelector(state => ({
    staticData: state.common.staticData,
  }));
  const { details: staticDetails } = staticData;

  const mappedCompanyTypes = useMemo(
    () => staticDataCompanyTypesMapper(staticDetails.companyTypes),
    [staticDetails],
  );
  const mappedIndustries = useMemo(
    () => staticDataIndustriesMapper(staticDetails.industries),
    [staticDetails],
  );
  const mappedCountries = useMemo(
    () => staticDataCountriesMapper(staticDetails.countries),
    [staticDetails],
  );

  const themeContext = useContext(ThemeContext);
  const { colors } = themeContext;

  const companyActivationGridHeaderList =
    companyActivationStrings.companyActivationList.companyActivationGridHeader;

  return (
    <>
      <Wrapper>
        <GridWrapper>
          <Divider />
          {Object.keys(companyActivationGridHeaderList).map(companyActivationGridHeaderKey => (
            <Header key={companyActivationGridHeaderKey}>
              {companyActivationGridHeaderList[companyActivationGridHeaderKey]}
            </Header>
          ))}
          <Divider />
          {companyActivationListArr.map((company, index) => {
            const companyStatusId = company.licenceStatus.licenceStatusId;

            const companyStatusColor = companyStatusColorEnum(colors)[companyStatusId].color;
            const companyStatusBackgroundColor =
              companyStatusColorEnum(colors)[companyStatusId].backgroundColor;

            const isBrandProfile = !!company.parentCompanyProfile;

            const contact = company.contacts[0] || {};
            const { name, email, phone } = contact;

            const isLastItem = index === companyActivationListArr.length - 1;

            return (
              <React.Fragment key={company.companyProfileKey}>
                <ItemColumn>
                  <ItemClick
                    onClick={() => handleTextToCopyToClipboardClick(company.companyProfileKey)}
                  >
                    {trimText(company.companyProfileKey, 10)}
                  </ItemClick>
                </ItemColumn>
                <ItemColumn>
                  <ItemText primaryText={isBrandProfile}>{company.name}</ItemText>
                  {isBrandProfile && (
                    <ItemSubText bold>
                      {
                        companyActivationStrings.companyActivationList
                          .companyActivationBrandProfileText
                      }
                    </ItemSubText>
                  )}
                </ItemColumn>
                <ItemColumn>
                  <ItemWrapper>
                    <ItemTextWrapper
                      backgroundColor={companyStatusBackgroundColor}
                      borderColor={companyStatusColor}
                    >
                      <ItemText color={companyStatusColor} inline>
                        {company.email}
                      </ItemText>
                    </ItemTextWrapper>
                  </ItemWrapper>
                </ItemColumn>
                <ItemColumn>
                  <ItemText>{mappedCompanyTypes[company.companyTypeKey]}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  <ItemText>{mappedIndustries[company.industryKey]}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  <ItemText>{mappedCountries[company.countryKey]}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  <ItemWrapper>
                    <ItemText>{name}</ItemText>
                    <SectionDivider gap={2} />
                    <ItemSubText>{email}</ItemSubText>
                    <SectionDivider gap={2} />
                    <ItemSubText>{phone}</ItemSubText>
                  </ItemWrapper>
                </ItemColumn>
                <ItemRow padding>
                  {company.licences.map(licence => {
                    const licenceNameArr = licence.split('.');
                    const licenceExtension = licenceNameArr[licenceNameArr.length - 1];
                    const licenceType = detectFileTypeFromExtension(licenceExtension);
                    const licenceTypeName = fileTypeEnum.getKeyByValue(licenceType);

                    return (
                      <ItemTag
                        key={licence}
                        margin
                        hover
                        onClick={() => handlePreviewFileClick(true, licence, licenceType)}
                      >
                        {licenceTypeName}
                      </ItemTag>
                    );
                  })}
                </ItemRow>
                <ItemRow>
                  {(companyStatusId === companyStatusEnum.Pending ||
                    companyStatusId === companyStatusEnum.Rejected) &&
                    isBrandProfile === false && (
                      <ItemBtn
                        as="button"
                        onClick={
                          () =>
                            handleActionBtnClick(
                              company.companyProfileKey,
                              companyStatusEnum.getKeyByValue(2),
                            ) // 2 is Verified
                        }
                      >
                        {
                          companyActivationStrings.companyActivationList
                            .companyActivationGridVerifyActionBtn
                        }
                      </ItemBtn>
                    )}
                  {(companyStatusId === companyStatusEnum.Pending ||
                    companyStatusId === companyStatusEnum.Verified) &&
                    isBrandProfile === false && (
                      <ItemBtn
                        as="button"
                        onClick={
                          () =>
                            handleActionBtnClick(
                              company.companyProfileKey,
                              companyStatusEnum.getKeyByValue(3),
                            ) // 3 is Rejected
                        }
                      >
                        {
                          companyActivationStrings.companyActivationList
                            .companyActivationGridRejectActionBtn
                        }
                      </ItemBtn>
                    )}
                </ItemRow>
                {isLastItem === false && <Divider />}
              </React.Fragment>
            );
          })}
        </GridWrapper>
      </Wrapper>
      {isActionPopupOpen && (
        <CompanyActionsPopup
          themeContext={themeContext}
          isActionPopupLoading={isActionPopupLoading}
          actionStatus={actionPopupStatus}
          handleActionPopupConfirmBtnClick={handleActionPopupConfirmBtnClick}
          handleActionPopupDismissBtnClick={handleActionPopupDismissBtnClick}
        />
      )}
    </>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

CompanyActivationList.propTypes = {
  companyActivationListArr: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isActionPopupOpen: PropTypes.bool.isRequired,
  isActionPopupLoading: PropTypes.bool.isRequired,
  actionPopupStatus: PropTypes.string.isRequired,
  handleTextToCopyToClipboardClick: PropTypes.func.isRequired,
  handlePreviewFileClick: PropTypes.func.isRequired,
  handleActionBtnClick: PropTypes.func.isRequired,
  handleActionPopupConfirmBtnClick: PropTypes.func.isRequired,
  handleActionPopupDismissBtnClick: PropTypes.func.isRequired,
};

CompanyActivationList.defaultProps = {};

export default CompanyActivationList;
